import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SearchHotelWithinRadiusResult from '@app/model/flexi_sets/SearchHotelWithinRadiusResult';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import SearchHotelsFilterInput, {
	emptySearchHotelsFilterInput,
} from '@app/model/flexi_sets/input/SearchHotelsFilterInput';
import FlexiSetsService from '@app/model/flexi_sets/service/FlexiSetsService';
import { SquaresPlusIcon } from '@heroicons/react/24/solid';
import _isEmpty from 'lodash/isEmpty';
import { TrashIcon } from '@heroicons/react/24/outline';
import FormButton from '@app/component/form/button/FormButton';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import useModal from '@app/component/overlays/modal/ModalRef';
import { useMutation } from '@tanstack/react-query';
import _clone from 'lodash/clone';
import { Link } from 'react-router-dom';
import FilterIndicatorsGroup
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__flexi-sets/component/FilterIndicatorsGroup';
import FlexiSetAreaHotelsListingFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__flexi-sets/fragment/FlexiSetAreaHotelsListingFragment';
import FlexiSetHotelsFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__flexi-sets/fragment/FlexiSetHotelsFragment';
import FlexiSetFiltersFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__flexi-sets/fragment/FlexiSetFiltersFragment';
import FlexiSetMapFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__flexi-sets/fragment/FlexiSetMapFragment';
import SaveOrCreateSetPageModal
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__flexi-sets/modal/SaveOrCreateSetPageModal';
import ImportAirbnbModal
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__flexi-sets/modal/ImportAirbnbModal';


const FlexiSetPage: React.FunctionComponent<{
	initialFlexiSetHotels?: SearchHotelWithinRadiusResult[],
	title: string,
	competitorId?: number
}> = ({ initialFlexiSetHotels = [], title, competitorId }) => {
	const { companyHotel } = useSelectedCompanyHotel();
	const [flexiSetHotels, setFlexiSetHotels] = useState<SearchHotelWithinRadiusResult[]>(initialFlexiSetHotels);

	const [areaHotels, setAreaHotels] = useState<SearchHotelWithinRadiusResult[]>([]);
	const [showList, setShowList] = useState<'AREA' | 'SET' | undefined>('AREA');

	const [isFiltering, setIsFiltering] = useState(false);
	const isEdit = useMemo(() => !!competitorId, [competitorId]);
	const toaster = useToaster();

	const modal = useModal();
	const LISTINGS_LIMIT = 30;
	const INITIAL_ZOOM_LEVEL = 16;

	const importModal = useModal();

	const [filter, setFilter] = useState<SearchHotelsFilterInput>(emptySearchHotelsFilterInput(companyHotel.id, companyHotel.mainHotel.competitors[0].hotel.location!.coordinates, 2000));

	useEffect(() => {
		setFlexiSetHotels(initialFlexiSetHotels);
	}, []);

	useEffect(() => {
		onFilter(filter);
	}, [filter]);

	const onViewportChange = (viewport: { longitude: number, latitude: number, zoom: number }) => {
		const copy = _clone(filter);
		copy.coordinates = { lng: viewport.longitude, lat: viewport.latitude };
		copy.radiusInMeters = 2000;
		if (Math.round(viewport.zoom) === 15) copy.radiusInMeters = 4000;
		else if (Math.round(viewport.zoom) === 14) copy.radiusInMeters = 8000;
		else if (viewport.zoom < 14) copy.radiusInMeters = 10000;
		setFilter(copy);
	};

	const findHotelsMutation = useMutation({
		mutationFn: FlexiSetsService.findHotelsAroundCoordinates,
		onSettled: () => setTimeout(() => setIsFiltering(false), 700),
		onSuccess: (hotels) => {
			setAreaHotels(hotels || []);
			setFilter(filter);
		},
		onMutate: () => setIsFiltering(true),
	});

	const onFilter = (filter: SearchHotelsFilterInput) => {
		findHotelsMutation.mutate(filter);
	};

	const reset = () => {
		setFlexiSetHotels(initialFlexiSetHotels);
	};

	const saveOrCreate = () => {
		if (_isEmpty(flexiSetHotels)) {
			toaster.error('Flexi Set', 'Please add at least one listing to the Set before saving!');
		} else if (flexiSetHotels.length > LISTINGS_LIMIT)
			toaster.error('Flexi Set', `You can't add more than ${LISTINGS_LIMIT} listings to the Set!`);
		else {
			modal.open();
		}
	};

	// Fast lookup of selected hotel IDs for checkbox rendering
	const selectedIds = useMemo(() => new Set(flexiSetHotels.map(h => h.id)), [flexiSetHotels]) || [];

	// Toggle handler to add/remove from flexiSetHotels with referential stability
	const onToggleHotel = useCallback((hotel: SearchHotelWithinRadiusResult, checked: boolean) => {
		setFlexiSetHotels(prev => {
			const exists = prev.some(h => h.id === hotel.id);
			if (checked) {
				return exists ? prev : [...prev, hotel];
			} else {
				return exists ? prev.filter(h => h.id !== hotel.id) : prev;
			}
		});
	}, []);

	const addAll = () => {
		areaHotels.forEach(hotel => {
			if (!selectedIds.has(hotel.id)) onToggleHotel(hotel, true);
		});
	};

	return <main>
		<div className="mx-auto py-6 px-4 sm:px-6 lg:px-8">
			<div className="mb-1 mt-3">
				<div className="sm:flex items-center justify-between gap-x-8">
					<div className="flex items-center gap-x-4">
						<div
							className="h-16 w-16 flex-none justify-items-center items-center self-center rounded-full ring-1 ring-gray-900/10 bg-white">
							<SquaresPlusIcon className="w-8 h-full self-center text-violet-600" />
						</div>
						<h3>
							<div
								className="text-lg font-semibold text-gray-900 tracking-tight">{title}
							</div>
							<div className="text-sm/6 text-gray-500">{flexiSetHotels.length}<span
								className="text-gray-700 ml-1">Listings</span>
								{!_isEmpty(flexiSetHotels) && <>
									{showList !== 'SET' && <a onClick={() => setShowList('SET')}
																						className="ml-1 text-xs font-medium tracking-tight  text-blue-600 hover:text-blue-900 hover:cursor-pointer">
										&bull; View selected listings
									</a>}
									{showList === 'SET' && <a onClick={() => setShowList(undefined)}
																						className="ml-1 text-xs font-medium tracking-tight  text-blue-600 hover:text-blue-900 hover:cursor-pointer">
										&bull; Close selected listings
									</a>}
								</>}
							</div>
						</h3>
					</div>
					<div className="flex flex-wrap items-center gap-x-4 gap-y-2">
						<Link to="/secured/rateshopper/rates/flexi-sets" className="text-sm/6 font-semibold text-gray-900">Go
							back</Link>
						{isEdit && <FormButton state="white" onClick={reset}>Reset</FormButton>}
						<button type="button" className="btn btn-primary" onClick={saveOrCreate}>{isEdit ? 'Save' : 'Create'} Set
						</button>
					</div>
				</div>
			</div>
			<div className="mt-4">
				<div className="flex flex-wrap items-center justify-between">
					<div className="flex flex-wrap items-center justify-start gap-x-3">
						<div className="flex items-center justify-start flex-wrap gap-x-2 gap-y-1">
							<FilterIndicatorsGroup filter={filter} onFilter={onFilter} />
						</div>
						{!filter.isEmpty() && <>
							<div className="h-4 w-px bg-gray-300 hidden sm:block"></div>
							<button className="btn btn-white h-6 px-2 py-0.5 text-xs font-medium" onClick={() => {
								filter.clear();
								onFilter(filter);
							}}>
								<TrashIcon className="h-3 w-3 mr-1" />
								<span>Clear All</span>
							</button>
						</>}

					</div>
					<div>
						<p className="text-sm text-gray-500">
							• Don't see a listing? <a onClick={()=>importModal.open()} className="font-semibold text-blue-600 hover:text-blue-500 cursor-pointer">
							Import with Airbnb Url</a>
						</p>
					</div>
				</div>
			</div>

			<div className="mt-4">
				<div className="grid gap-4 items-start md:grid-cols-[275px_auto_1fr]">
					{/* Left column */}
					<div className="wrapper">
						<FlexiSetFiltersFragment areaHotels={areaHotels}
																		 filter={filter}
																		 onFilter={onFilter}
																		 onShowListings={() => setShowList('AREA')}
																		 onAddToSet={addAll} />
					</div>
					<div className="wrapper">
						{showList === 'AREA' &&
							<FlexiSetAreaHotelsListingFragment
								areaHotels={areaHotels}
								selectedIds={selectedIds}
								onToggle={onToggleHotel}
								onClose={() => setShowList(undefined)}
							/>
						}
						{showList === 'SET' &&
							<FlexiSetHotelsFragment flexiSetHotels={flexiSetHotels}
																			onRemoveAllFromSet={() => setFlexiSetHotels([])}
																			onRemoveFromSet={(hotel) => onToggleHotel(hotel, false)}
																			onClose={() => setShowList(undefined)} />}

					</div>
					<div className="wrapper">
						<FlexiSetMapFragment initialZoomLevel={INITIAL_ZOOM_LEVEL}
																 areaHotels={areaHotels} selectedIds={selectedIds}
																 onAddToSet={(hotel) => onToggleHotel(hotel, true)}
																 onRemoveFromSet={(hotel) => onToggleHotel(hotel, false)}
																 isFiltering={isFiltering}
																 onRefreshResults={onViewportChange}
																 centralCoordinates={companyHotel.mainHotel.competitors[0].hotel.location!.coordinates} />
					</div>
				</div>
			</div>
		</div>
		<SaveOrCreateSetPageModal ref={modal.ref} companyHotelId={companyHotel.id} competitorId={competitorId}
															competitorsGroupName={title} flexiSetHotels={flexiSetHotels} />
		<ImportAirbnbModal ref={importModal.ref} width='large'/>
	</main>;
};

export default FlexiSetPage;